import React from "react";
import { Helmet } from "react-helmet-async";

const SEO = ({
  title = "Mihir Shah - OWASP VXDF Project Leader | Security Engineer & Author",
  description = "Project Leader for OWASP VXDF (Validated Exploitable Data Flow), transforming vulnerability data management. Security engineer specializing in cloud security, application security.",
  keywords = "Mihir Shah, OWASP VXDF, Validated Exploitable Data Flow, Security Engineer, Cloud Security, Application Security",
  image = "https://mihirshah.tech/og-image.jpg",
  url = "https://mihirshah.tech",
  type = "website",
  author = "Mihir Shah",
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  locale = "en_US",
  twitterHandle = "@mihirshah99",
  structuredData,
}) => {
  const siteTitle = title.includes("Mihir Shah") ? title : `${title} | Mihir Shah`;
  const siteName = "Mihir Shah - OWASP VXDF Project Leader";
  const baseUrl = "https://mihirshah.tech";
  
  // Enhanced keywords
  const enhancedKeywords = [
    ...keywords.split(", "),
    "OWASP Project Leader",
    "Cybersecurity Expert", 
    "DevSecOps",
    "Vulnerability Management",
    "Security Architecture",
    "Cloud Native Security"
  ].join(", ");

  return (
    <Helmet>
      {/* HTML Lang */}
      <html lang="en" />
      
      {/* Primary Meta Tags */}
      <title>{siteTitle}</title>
      <meta name="title" content={siteTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={enhancedKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Enhanced Viewport */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=1.0" />
      
      {/* Theme & App */}
      <meta name="theme-color" content="#1e40af" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="mobile-web-app-capable" content="yes" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link rel="dns-prefetch" href="https://github.com" />
      <link rel="dns-prefetch" href="https://linkedin.com" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={`${siteTitle} - Cover Image`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}
      {tags.length > 0 && tags.map(tag => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta property="article:author" content={author} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${siteTitle} - Cover Image`} />
      
      {/* LinkedIn */}
      <meta property="linkedin:owner" content="mihir-shah99" />
      
      {/* OWASP Specific Meta */}
      <meta name="owasp:project" content="VXDF - Validated Exploitable Data Flow" />
      <meta name="owasp:project-leader" content="Mihir Shah" />
      <meta name="owasp:project-url" content="https://vxdf.org" />
      <meta name="owasp:github" content="https://github.com/OWASP/VXDF" />
      
      {/* Schema.org Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Additional Rich Snippets */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": siteName,
          "url": baseUrl,
          "description": description,
          "author": {
            "@type": "Person",
            "name": author,
            "url": baseUrl,
            "sameAs": [
              "https://github.com/mihir-shah99",
              "https://linkedin.com/in/mihir-shah99",
              "https://x.com/mihirshah99"
            ]
          },
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${baseUrl}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
    </Helmet>
  );
};

export default SEO;
